import { Dealer } from '../dealer/models/dealer';
import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, of, tap } from 'rxjs';

import { RestDealerService } from '../dealer/services/rest-dealer.service';
import { DealerSettingService } from '../services/dealerSetting.service';
import { WorkOrderSettings } from '../../features/workorder/components/createworkorder/models/work-order-settings.model';
import _ from 'lodash-es';
import { FeatureFlags } from '../services/models/FeatureFlags';
import { Plant } from '../services/models/Plant';
import { Site } from '../services/models/Site';
import { Group } from '../../features/user-management/components/user/model/group';
import { PlantSetup } from '../../features/setup-screens/models/plant-setup.model';
import { WorkOrderRequiredFieldFlags } from '../services/models/WorkOrderRequiredFieldFlags';
import { DealerConfigs } from '../services/models/DealerConfigs';
import { ZoneSettings } from '../services/models/ZoneSettings';
import { FEATURE_FLAGS } from '../constants/constants';
import { resetFaroUser, setFaroUser } from '../utility/faro-session.util';

export class FetchAllDealersAction {
  static readonly type = '[Dealer] DealersFetched';
}

export class UpdateCurrentDealerAction {
  static readonly type = '[Dealer] CurrentDealer Updated';

  constructor(public selectedDealer: Dealer, public user: string) {}
}

export class UpdateDealerFlagsAction {
  static readonly type = '[Dealer] DealerFlags Updated';
  constructor(public flags: FeatureFlags) {}
}

export class UpdatePlantsAction {
  static readonly type = '[Dealer] Plants Updated';

  constructor(public plants: Array<Plant>) {}
}

export class UpdateSelectedPlantAction {
  static readonly type = '[Dealer] Selected Plant Updated';

  constructor(public selectedPlant: PlantSetup) {}
}

export class UpdateSitesAction {
  static readonly type = '[Dealer] Sites Updated';

  constructor(public sites: Array<Site>) {}
}

export class UpdateGroupsAction {
  static readonly type = '[Dealer] User Groups Updated';

  constructor(public groups: Array<Group>) {}
}

export class UpdatePosEnabledAction {
  static readonly type = '[Dealer] POS Enabled Updated';

  constructor(public posEnabled: boolean) {}
}

export class UpdateDealerConfigsAction {
  static readonly type = '[Dealer] Dealer Configs Updated';

  constructor(public dealerConfigs: DealerConfigs) {}
}

export class UpdateWorkOrderSettingsAction {
  static readonly type = '[Dealer] Work Order Settings Updated';

  constructor(public settings: WorkOrderSettings) {}
}

export interface DealerStateModel {
  loading: boolean;
  currentDealer: Dealer;
  allDealers: Array<Dealer>;
  loggedInUser: string;
  password_dt: string;
  featureFlags: FeatureFlags | null;
  plants: Array<Plant>;
  selectedPlant: PlantSetup;
  sites: Array<Site>;
  groups: Array<Group>;
  dealerConfigs: DealerConfigs;
  workOrderSettings: WorkOrderSettings | null;
}

export class ResetDealerStateAction {
  static readonly type = '[Dealer] Dealer State Reset';
}

const defaultState = {
  loading: true,
  currentDealer: null as Dealer,
  allDealers: [] as Dealer[],
  loggedInUser: '' as string,
  password_dt: '' as string,
  featureFlags: { flags: new Map<string, boolean>() },
  plants: [] as Plant[],
  selectedPlant: null as PlantSetup,
  sites: [] as Site[],
  groups: [] as Group[],
  dealerConfigs: {} as DealerConfigs,
  workOrderSettings: null as WorkOrderSettings | null
};

const getDefaultState = () => {
  return { ...defaultState };
};

@State<DealerStateModel>({
  name: 'dealer',
  defaults: getDefaultState()
})
@Injectable()
export class DealerState {
  private readonly restDealerService = inject(RestDealerService);
  private readonly dealerSettingService = inject(DealerSettingService);
  constructor() {}

  @Action(FetchAllDealersAction)
  fetchAllDealersAction({ patchState }: StateContext<DealerStateModel>) {
    patchState({ loading: true });
    return this.restDealerService.getAllDealers().pipe(
      tap((res) => {
        let allDealers = res.data;
        allDealers = _.orderBy(allDealers, ['name'], ['asc']);
        patchState({ allDealers, loading: false });
      }),

      catchError((error) => {
        patchState(getDefaultState());
        throw error;
      })
    );
  }

  @Action(UpdateCurrentDealerAction)
  updateCurrentDealerAction(ctx: StateContext<DealerStateModel>, action: UpdateCurrentDealerAction) {
    ctx.patchState({ loading: true });
    const currentDealer = action.selectedDealer;
    ctx.patchState({ currentDealer, loading: false, loggedInUser: action.user });
    setFaroUser(currentDealer?.name, action.user);
    // Fetch dealer configs (incl. zone logo) immediately so it's available as soon as the shell renders
    return this.dealerSettingService.getDealerConfigs(currentDealer.id).pipe(
      tap((dealerConfigs) => ctx.patchState({ dealerConfigs })),
      catchError(() => of(null))
    );
  }

  @Action(ResetDealerStateAction)
  resetDealerStateAction(ctx: StateContext<DealerStateModel>) {
    ctx.patchState(getDefaultState());
    resetFaroUser();
  }

  @Action(UpdateDealerFlagsAction)
  updateDealerFlags(ctx: StateContext<DealerStateModel>, action: UpdateDealerFlagsAction) {
    ctx.patchState({ featureFlags: action.flags });
  }

  @Action(UpdatePlantsAction)
  updatePlantsAction(ctx: StateContext<DealerStateModel>, action: UpdatePlantsAction) {
    ctx.patchState({ plants: action.plants });
  }

  @Action(UpdateSelectedPlantAction)
  updateSelectedPlantAction(ctx: StateContext<DealerStateModel>, action: UpdateSelectedPlantAction) {
    ctx.patchState({ selectedPlant: action.selectedPlant });
  }

  @Action(UpdateSitesAction)
  updateSitesAction(ctx: StateContext<DealerStateModel>, action: UpdateSitesAction) {
    ctx.patchState({ sites: action.sites });
  }

  @Action(UpdateGroupsAction)
  updateGroupsAction(ctx: StateContext<DealerStateModel>, action: UpdateGroupsAction) {
    ctx.patchState({ groups: action.groups });
  }

  @Action(UpdatePosEnabledAction)
  updatePosEnabledAction(ctx: StateContext<DealerStateModel>, action: UpdatePosEnabledAction) {
    const currentDealer = ctx.getState().currentDealer;
    if (currentDealer) {
      ctx.patchState({ currentDealer: { ...currentDealer, posEnabled: action.posEnabled } });
    }
  }

  @Action(UpdateDealerConfigsAction)
  updateWorkOrderRequiredFieldsAction(ctx: StateContext<DealerStateModel>, action: UpdateDealerConfigsAction) {
    ctx.patchState({ dealerConfigs: action.dealerConfigs });
  }

  @Action(UpdateWorkOrderSettingsAction)
  updateWorkOrderSettingsAction(ctx: StateContext<DealerStateModel>, action: UpdateWorkOrderSettingsAction) {
    ctx.patchState({ workOrderSettings: action.settings });
  }

  @Selector()
  static state(state: DealerStateModel) {
    return state ?? getDefaultState();
  }

  @Selector()
  static currentDealer(state: DealerStateModel) {
    return state?.currentDealer ?? null;
  }

  @Selector()
  static loading(state: DealerStateModel) {
    return state?.loading ?? true;
  }

  @Selector()
  static allDealers(state: DealerStateModel) {
    return state?.allDealers ?? [];
  }

  @Selector()
  static currentUser(state: DealerStateModel) {
    return state?.loggedInUser ?? '';
  }

  @Selector()
  static featureFlags(state: DealerStateModel): FeatureFlags | null {
    return state?.featureFlags ?? null;
  }

  @Selector()
  static bannersEnabled(state: DealerStateModel): boolean {
    const flags = state?.featureFlags?.flags;
    const value = flags instanceof Map ? flags.get(FEATURE_FLAGS.BANNERS_ENABLED) : flags?.[FEATURE_FLAGS.BANNERS_ENABLED];
    return value !== false;
  }

  @Selector()
  static plants(state: DealerStateModel) {
    return state?.plants ?? [];
  }

  @Selector()
  static selectedPlant(state: DealerStateModel) {
    return state?.selectedPlant ?? null;
  }

  @Selector()
  static sites(state: DealerStateModel) {
    return state?.sites ?? [];
  }

  @Selector()
  static groups(state: DealerStateModel) {
    return state?.groups ?? [];
  }

  @Selector()
  static posEnabled(state: DealerStateModel): boolean {
    return !!state?.currentDealer?.posEnabled;
  }

  @Selector()
  static dealerConfigs(state: DealerStateModel) {
    return state?.dealerConfigs ?? ({} as DealerConfigs);
  }

  @Selector()
  static zoneSettings(state: DealerStateModel): ZoneSettings | null {
    return state?.dealerConfigs?.zoneSettings ?? null;
  }

  @Selector()
  static workOrderRequiredFieldFlags(state: DealerStateModel): WorkOrderRequiredFieldFlags | null {
    return state?.dealerConfigs?.zoneSettings?.workOrderRequiredFields ?? null;
  }

  @Selector()
  static zoneImage(state: DealerStateModel): string | null {
    return state.dealerConfigs?.zoneSettings?.zoneImage ?? null;
  }

  @Selector()
  static zoneImageUrl(state: DealerStateModel): string | null {
    const zoneImage = state?.dealerConfigs?.zoneSettings?.zoneImage;
    return zoneImage ? `data:image/png;base64,${zoneImage}` : null;
  }

  @Selector()
  static workOrderSettings(state: DealerStateModel): WorkOrderSettings | null {
    return state?.workOrderSettings ?? null;
  }
}
